import { useCallback, useEffect, useState } from "react";
import type { AdminContestEntry } from "@shared";
import * as api from "./adminApi";

interface Props {
  eventId: string;
  /** Le concours est-il activé sur cette mission ? Réglé dans l'onglet Missions. */
  enabled: boolean;
}

/**
 * Les participations au concours.
 *
 * Deux fonctions, et elles vont ensemble : LIRE les adresses pour tirer les
 * gagnants, et les SUPPRIMER une fois le tirage fait. La seconde n'est pas une
 * commodité — la page de consentement promet à l'adulte que son adresse sera
 * effacée après le tirage. Sans ce bouton, la promesse serait intenable.
 */
export default function ContestPanel({ eventId, enabled }: Props) {
  const [entries, setEntries] = useState<AdminContestEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [purging, setPurging] = useState(false);
  const [password, setPassword] = useState("");

  const refresh = useCallback(async () => {
    try {
      setEntries(await api.listContestEntries(eventId));
    } catch {
      setError("Impossible de charger les participations.");
    }
  }, [eventId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function purge(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setMessage(null);
    try {
      const { deleted } = await api.purgeContestEntries(eventId, password);
      setMessage(
        deleted === 0
          ? "Aucune participation à supprimer."
          : `${deleted} participation${deleted > 1 ? "s" : ""} supprimée${deleted > 1 ? "s" : ""}.`,
      );
      setPassword("");
      setPurging(false);
      await refresh();
    } catch {
      setError("Suppression refusée. Vérifie le mot de passe administrateur.");
    }
  }

  /**
   * Télécharge les participations en CSV — adresses et dates.
   *
   * Le fichier se fabrique ICI, dans le navigateur, à partir de la liste déjà
   * chargée : pas de route de plus, et surtout pas d'URL contenant des adresses
   * courriel, qui finirait dans l'historique du navigateur et dans les journaux
   * du serveur.
   *
   * Point-virgule comme séparateur : c'est ce qu'attend un Excel configuré en
   * français, et une adresse ne contient jamais de point-virgule.
   */
  function telecharger(): void {
    if (entries === null || entries.length === 0) return;

    const echappe = (v: string): string => `"${v.replace(/"/g, '""')}"`;
    const lignes = [
      ["Adresse courriel", "Date de participation", "Langue du consentement", "Version du texte"],
      ...entries.map((e) => [
        e.email,
        new Date(e.createdAt).toLocaleString("fr-CA"),
        e.lang,
        e.consentVersion,
      ]),
    ]
      .map((colonnes) => colonnes.map(echappe).join(";"))
      .join("\r\n");

    // BOM en tête : sans lui, Excel lit l'UTF-8 comme du latin-1 et massacre
    // les accents d'une adresse ou d'une date.
    const blob = new Blob(["﻿" + lignes], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const lien = document.createElement("a");
    lien.href = url;
    lien.download = `concours-${new Date().toISOString().slice(0, 10)}.csv`;
    lien.click();
    URL.revokeObjectURL(url);
  }

  return (
    <section className="panel">
      <h2 className="panel__title">Participations au concours</h2>

      {!enabled && (
        <p className="form__warning">
          Le concours n'est pas activé sur cette mission : le jeune ne le voit pas. L'interrupteur
          est dans l'onglet « Missions ». Les participations déjà enregistrées restent listées
          ci-dessous.
        </p>
      )}

      <p className="panel__hint">
        Ces adresses sont la <strong>seule donnée personnelle</strong> de l'application. Le texte
        affiché à l'adulte promet qu'elles servent uniquement à contacter la famille dragonaute gagnante, et qu'elles
        sont supprimées après le tirage. Le bouton du bas est ce qui permet de tenir cette promesse.
      </p>

      {error !== null && <p className="form__error">{error}</p>}
      {message !== null && <p className="form__saved">{message}</p>}

      {entries === null && <p className="panel__hint">Chargement…</p>}

      {entries !== null && (
        <>
          <p className="panel__hint">
            <strong>{entries.length}</strong>{" "}
            {entries.length > 1 ? "participations" : "participation"}
            {entries.length > 0 && " — une adresse ne peut participer qu'une fois."}
          </p>

          {/* Le téléchargement vit ICI, derrière le mot de passe admin, et non
              sur la page de statistiques : celle-ci compte, elle ne nomme
              personne. */}
          {entries.length > 0 && (
            <div className="button-row button-row--top">
              <button type="button" className="button button--small" onClick={telecharger}>
                ⭳ Télécharger en CSV (adresses et dates)
              </button>
            </div>
          )}

          <ul className="list">
            {entries.map((entry) => (
              <li className="list__item" key={entry.id}>
                <div className="list__main">
                  <span className="list__name">{entry.email}</span>
                  <span className="list__meta">
                    {new Date(entry.createdAt).toLocaleString()} · consentement {entry.lang}
                    {" · "}
                    <span title="Empreinte du texte accepté">{entry.consentVersion}</span>
                  </span>
                </div>
              </li>
            ))}
            {entries.length === 0 && <li className="list__empty">Aucune participation.</li>}
          </ul>
        </>
      )}

      {/* Confirmation forte : retaper le mot de passe, comme pour les purges.
          Être connecté ne suffit pas pour une action irréversible. */}
      {entries !== null && entries.length > 0 && (
        <div className="panel__danger">
          {!purging && (
            <button
              type="button"
              className="button button--danger"
              onClick={() => {
                setPurging(true);
                setMessage(null);
              }}
            >
              Supprimer toutes les participations
            </button>
          )}

          {purging && (
            <form className="form" onSubmit={(e) => void purge(e)}>
              <p className="form__warning">
                Cette action est <strong>irréversible</strong> : les {entries.length} adresses seront
                effacées. À faire une fois la famille dragonaute gagnante contactée.
              </p>
              <label className="form__label" htmlFor="contest-purge-password">
                Retape le mot de passe administrateur pour confirmer
              </label>
              <input
                id="contest-purge-password"
                className="form__input"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                }}
              />
              <div className="button-row">
                <button type="submit" className="button button--danger" disabled={password === ""}>
                  Supprimer définitivement
                </button>
                <button
                  type="button"
                  className="button button--ghost"
                  onClick={() => {
                    setPurging(false);
                    setPassword("");
                  }}
                >
                  Annuler
                </button>
              </div>
            </form>
          )}
        </div>
      )}
    </section>
  );
}
